import { Menu as HeadlessMenu, MenuButton, MenuItems } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { Fragment, ReactNode } from 'react';
import { MenuItem, MenuItemType } from './components/MenuItem';

export type MenuProps = {
  items?: MenuItemType[];
  buttonContent?: ReactNode;
  disableAutoClose?: boolean;
  isStatic?: boolean;
  renderMenuItems?: (close: () => void) => ReactNode;
  /**
   * Custom button render (overrides `buttonContent` prop)
   */
  renderMenuButton?: (open?: boolean) => ReactNode;
};

export const Menu = ({
  items,
  buttonContent,
  disableAutoClose,
  isStatic,
  renderMenuItems,
  renderMenuButton,
}: MenuProps) => {
  const handleItemClick = (item: MenuItemType, close: () => void) => {
    if ('isDivider' in item && item.isDivider) return;
    if (item.disabled) return;

    if (!disableAutoClose) close();

    if (item.onClick) {
      item.onClick();
    }
  };

  return (
    <HeadlessMenu as="div" className="relative">
      {({ close, open }) => (
        <>
          {renderMenuButton ? (
            <MenuButton as={Fragment}>{renderMenuButton(open)}</MenuButton>
          ) : (
            <MenuButton className="inline-flex w-full items-center justify-center bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-white/75">
              {buttonContent}
              <ChevronDownIcon
                className={clsx(
                  'size-4 text-gray-500',
                  buttonContent && 'ml-2',
                )}
                aria-hidden="true"
              />
            </MenuButton>
          )}

          <MenuItems
            transition
            static={isStatic}
            className="absolute right-0 z-10 mt-2 w-56 origin-top-right divide-y divide-gray-200 rounded-md bg-white shadow-lg transition focus:outline-hidden data-closed:scale-95 data-closed:transform data-closed:opacity-0 data-enter:duration-200 data-enter:ease-out data-leave:duration-75 data-leave:ease-in"
          >
            {renderMenuItems ? (
              <>{renderMenuItems(close)}</>
            ) : (
              <div className="py-1">
                {items?.map((item) => (
                  <MenuItem
                    key={item.id}
                    item={item}
                    handleItemClick={handleItemClick}
                    disableAutoClose={disableAutoClose}
                  />
                ))}
              </div>
            )}
          </MenuItems>
        </>
      )}
    </HeadlessMenu>
  );
};
